import { Flex, Button, Text, useDisclosure } from "@chakra-ui/react";
import PageTitle from "../components/PageTitle";
import { AddIcon } from "@chakra-ui/icons";
import SimpleModal from "../components/modals/SimpleModal";
import AddFileIcon from "../components/icons/AddFileIcon";
import DocumentationForm, {
	type DocumentationFormHandler,
} from "../components/forms/DocumentationForm";
import type { Documentation } from "../types/documentation.types";
import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import {
	createDocumentation,
	deleteDocumentation,
} from "../mutations/documentation.mutations";
import { useSimpleToast } from "../hooks/toast.hooks";
import DataTable, { type Cell } from "../components/table/ServerSideTable";
import { useDocumentationsPaginated } from "../hooks/documentation.hooks";
import ValidationModal from "../components/modals/ValidationModal";
import FileIcon from "../components/icons/FileIcon";
import PrimaryButton from "../components/PrimaryButton";
import { fetchFileContent } from "../mutations/file.mutations";
import { useConnectedUser } from "../hooks/user.hooks";
import { getDocumentationColumns } from "./DocumentationPageColumns";

type DocumentationFormProps = {
	value?: Documentation;
};

function DocumentationPage({ value }: DocumentationFormProps) {
	const user = useConnectedUser();
	const contentModal = useDisclosure();
	const contentDeletionModal = useDisclosure();
	const contentFormRef = useRef<DocumentationFormHandler>(null);
	const toast = useSimpleToast();
	const queryClient = useQueryClient();
	const create = useMutation(createDocumentation, {
		onSuccess: () => {
			queryClient.invalidateQueries("documentations-paginated");
			contentModal.onClose();
			setSelectedDoc(null);
		},
		onError: () => {
			toast.error(
				"Sauvegarde",
				"Erreur lors de la sauvegarde de la documentation.",
			);
		},
	});
	const deletion = useMutation(deleteDocumentation, {
		onSuccess: () => {
			queryClient.invalidateQueries("documentations-paginated");
			contentDeletionModal.onClose();
			setSelectedDoc(null);
		},
	});
	const [selectedDoc, setSelectedDoc] = useState<Documentation | null>(null);
	const columns = getDocumentationColumns({
		isSuperAdmin: user?.isSuperAdmin === true,
		onDelete: (documentation) => {
			setSelectedDoc(documentation);
			contentDeletionModal.onOpen();
		},
		onEdit: (documentation) => {
			setSelectedDoc(documentation);
			contentModal.onOpen();
		},
	});
	const DocumentationTableNoData = () => {
		return (
			<Flex justify="center">
				<Flex w="100%" justifyContent="center">
					<Flex
						w="100%"
						rounded="xl"
						bg="white"
						textAlign="center"
						lineHeight="44px"
						color="gray.500"
						direction={"column"}
						justifyContent={"center"}
						alignItems={"center"}
						py="100px"
					>
						<Flex
							w="48px"
							h="48px"
							bg="zinc.50"
							border="solid 1px"
							borderColor="brand.100"
							justifyContent="center"
							alignItems="center"
							borderRadius={100}
							mr="8px"
						>
							<FileIcon w="24px" h="24px" color="brand.700"></FileIcon>
						</Flex>
						<Text
							mt={4}
							color={"gray.900"}
							fontSize={"20px"}
							lineHeight={"30px"}
							fontWeight={600}
						>
							Aucune ressource
						</Text>
						{user?.isSuperAdmin && (
							<PrimaryButton w="30rem" mt={4} onClick={contentModal.onOpen}>
								Ajouter une ressource
							</PrimaryButton>
						)}
					</Flex>
				</Flex>
			</Flex>
		);
	};
	const fileName = useRef<string>("");
	const { mutate: downloadFileContent } = useMutation(fetchFileContent, {
		onSuccess: (content: string) => {
			if (!fileName.current) {
				return;
			}
			const blob = new Blob([content]);
			const link = document.createElement("a");
			link.href = URL.createObjectURL(blob);
			link.download = fileName.current;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		},
	});

	const handleCreateAndUpdate = () => {
		if (!contentFormRef.current) {
			return;
		}
		const valid = contentFormRef.current.valid();
		if (valid) {
			const values = contentFormRef.current.values();
			create.mutate(values);
		}
	};
	return (
		<>
			<Flex justify="space-between">
				<PageTitle>Documentation</PageTitle>
				{user?.isSuperAdmin && (
					<Flex>
						<Button
							variant="solid"
							bgColor="brand.600"
							color="white"
							onClick={contentModal.onOpen}
						>
							<AddIcon mr={4}></AddIcon>Ajouter du contenu
						</Button>
					</Flex>
				)}

				<SimpleModal
					icon={<AddFileIcon color="brand.700"></AddFileIcon>}
					title="Ajouter du contenu"
					isOpen={contentModal.isOpen}
					onClose={contentModal.onClose}
					w="min(1000px, 80vw)"
					onClick={handleCreateAndUpdate}
				>
					<DocumentationForm ref={contentFormRef} value={value} />
				</SimpleModal>

				<ValidationModal
					description={"Voulez-vous supprimer ce document ?"}
					isOpen={contentDeletionModal.isOpen}
					onClose={() => {
						setSelectedDoc(null);
						contentDeletionModal.onClose();
					}}
					onValidate={() => {
						if (selectedDoc) {
							deletion.mutate({ id: selectedDoc.id });
						}
					}}
				></ValidationModal>
			</Flex>

			<DataTable
				columns={columns}
				paginatedQuery={useDocumentationsPaginated}
				flexGrow={[1, 0, 0]}
				w={["auto", "8em", "100px"]}
				noDataComponent={DocumentationTableNoData}
				cellLink={(cell: Cell) => {
					if (cell.id.endsWith("action")) {
						return;
					}
					switch (cell.row.type) {
						case "link": {
							return cell.row.link;
						}
						default:
							return;
					}
				}}
				handleCellClick={(cell: Cell) => {
					if (cell.id.endsWith("action")) {
						return;
					}
					switch (cell.row.type) {
						case "file": {
							if (cell.row.file) {
								fileName.current = cell.row.file.fileName;
								downloadFileContent(cell.row.fileId);
							} else {
								toast.error(
									"Pas de fichier",
									"Aucun fichier n'est lié à cette documentation",
								);
							}
							break;
						}
						default:
							break;
					}
				}}
			></DataTable>
		</>
	);
}

export default DocumentationPage;
